// eslint-disable-next-line no-restricted-imports
import { Formatter, query, zero } from 'fp-ts-routing';

import { iots } from '@code-expert/prelude';
import { ProjectEntryKey, ProjectId, ProjectUploadFileId, SnapshotId } from '/imports/domain';
import type { UnwrapMatch } from '/imports/modules/Routing';
import { end, formatRoute, lit, Match, path, type, url } from '/imports/modules/Routing';
import { MessagesSection } from '/imports/ui/pages/myCourses/messages/navigation';

const ide2 = lit('ide2');
const projectId = type('projectId', ProjectId);
const solution = lit('solution');

const queryParams = query(
  iots.exact(
    iots.partial({
      fileKey: ProjectEntryKey,
      snapshotId: SnapshotId,
      section: MessagesSection,
    }),
  ),
);

const mainRoute = url(ide2, projectId, queryParams, end);
const solutionRoute = url(ide2, projectId, solution, queryParams, end);

export type IdeRouteParams = UnwrapMatch<typeof mainRoute> & { showSolution: boolean };

export const ideRoute = new Match<IdeRouteParams>(
  zero<IdeRouteParams>()
    .alt(mainRoute.parser.map((p) => ({ ...p, showSolution: false })))
    .alt(solutionRoute.parser.map((p) => ({ ...p, showSolution: true }))),
  new Formatter((route, { showSolution, projectId, snapshotId, fileKey, section }) =>
    (showSolution ? solutionRoute.formatter : mainRoute.formatter).run(route, {
      projectId,
      fileKey,
      snapshotId,
      section,
    }),
  ),
);

export type IdeNavigationParams = Omit<IdeRouteParams, 'showSolution'> & { showSolution?: boolean };
export type IdeNavigation = (_: IdeNavigationParams) => string;
export type PartialIdeNavigation = (_: Partial<IdeNavigationParams>) => string;

export const ideLink: IdeNavigation = ({
  projectId,
  fileKey,
  snapshotId,
  showSolution = false,
  section,
}) => formatRoute(ideRoute)({ projectId, fileKey, snapshotId, section, showSolution });

//--------------------------------------------------------------------------------------------------

const addToProjectFilesRoute = url(
  lit('addToProjectFiles'),
  projectId,
  type('parentKey', ProjectEntryKey),
  end,
);

export const addToProjectFilesLink = formatRoute(addToProjectFilesRoute);

//--------------------------------------------------------------------------------------------------

const cxProjectFile = lit('cx_project_file');
const fileId = type('fileId', ProjectEntryKey);
const fileName = type('fileName', iots.string);

const cxProjectFileRoute = url(cxProjectFile, fileId, end);
const cxProjectFileWithNameRoute = url(cxProjectFile, fileId, fileName, end);

export const cxProjectFileLink = ({
  fileId,
  fileName,
}: {
  fileId: ProjectEntryKey;
  fileName?: string;
}) =>
  fileName == null
    ? formatRoute(cxProjectFileRoute)({ fileId })
    : formatRoute(cxProjectFileWithNameRoute)({ fileId, fileName });

//--------------------------------------------------------------------------------------------------

const v2 = lit('v2');

const cxProjectFileV2Route = url(cxProjectFile, v2, projectId, path('path'), end);

export const cxProjectFileV2Link = formatRoute(cxProjectFileV2Route);

//--------------------------------------------------------------------------------------------------

const cxOut = lit('cx_out');
const uploadFileId = type('fileId', ProjectUploadFileId);

export const cxOutRoute = url(cxOut, uploadFileId, end);

export const cxOutLink = formatRoute(cxOutRoute);

//--------------------------------------------------------------------------------------------------

const uploadToProjectUpload = lit('uploadToProjectUpload');

export const uploadToProjectUploadRoute = url(uploadToProjectUpload, end);

export const uploadToProjectUploadLink = formatRoute(uploadToProjectUploadRoute)({});

//--------------------------------------------------------------------------------------------------
